import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProjetos } from '../hooks/useProjetos';
import { cores } from '../styles/cores';
import {
  Avatar,
  BadgeCategoria,
  BadgeStatus,
  BarraProgresso,
  Btn,
  Campo,
  EstadoVazio,
  Input,
  Modal,
  Select,
  Spinner,
} from '../components/UI';

const FILTROS_STATUS = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'em_andamento', rotulo: 'Em andamento' },
  { valor: 'aguardando', rotulo: 'Aguardando' },
  { valor: 'pausado', rotulo: 'Pausado' },
  { valor: 'atrasado', rotulo: 'Atrasado' },
  { valor: 'concluida', rotulo: 'Concluído' },
];

const CATEGORIAS = ['Desenvolvimento', 'Infra', 'Design', 'Dados', 'Outros'];

const STATUS_OPCOES = [
  { valor: 'aguardando', rotulo: 'Aguardando' },
  { valor: 'em_andamento', rotulo: 'Em andamento' },
  { valor: 'pausado', rotulo: 'Pausado' },
  { valor: 'atrasado', rotulo: 'Atrasado' },
  { valor: 'concluida', rotulo: 'Concluído' },
];

const FORM_VAZIO = {
  nome: '',
  descricao: '',
  categoria: 'Desenvolvimento',
  status: 'aguardando',
  responsavel: '',
  membros: '',
  dataInicio: '',
  dataPrevisao: '',
};

const paraDataInput = (dataBR) => {
  if (!dataBR) return '';
  const [dia, mes, ano] = dataBR.split('/');
  if (!dia || !mes || !ano) return '';
  return `${ano}-${mes}-${dia}`;
};

const paraDataBR = (dataInput) => {
  if (!dataInput) return '';
  const [ano, mes, dia] = dataInput.split('-');
  return `${dia}/${mes}/${ano}`;
};

const ORDENACOES = [
  { campo: 'nome',       rotulo: 'Nome',      asc: 'A → Z',     desc: 'Z → A' },
  { campo: 'percentual', rotulo: 'Conclusão', asc: '↑ Menor',   desc: '↓ Maior' },
  { campo: 'data',       rotulo: 'Data',      asc: '↑ Próxima', desc: '↓ Distante' },
];

const parseDateBR = (dataBR) => {
  if (!dataBR) return 0;
  const [dia, mes, ano] = dataBR.split('/');
  if (!dia || !mes || !ano) return 0;
  return new Date(`${ano}-${mes}-${dia}`).getTime() || 0;
};

export function Projetos() {
  const { usuario } = useAuth();
  const { projetos, carregando, criarProjeto, atualizarProjeto, excluirProjeto } = useProjetos();
  const navegar = useNavigate();

  const [filtro, setFiltro] = useState('todos');
  const [ordenacao, setOrdenacao] = useState({ campo: '', ordem: 'asc' });
  const [modalAberto, setModalAberto] = useState(false);
  const [projetoEditando, setProjetoEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);

  const projetosFiltrados =
    filtro === 'todos' ? projetos : projetos.filter((p) => p.status === filtro);

  const alternarOrdenacao = (campo) => {
    setOrdenacao((prev) =>
      prev.campo === campo
        ? { campo, ordem: prev.ordem === 'asc' ? 'desc' : 'asc' }
        : { campo, ordem: 'asc' }
    );
  };

  const projetosOrdenados = ordenacao.campo
    ? [...projetosFiltrados].sort((a, b) => {
        const mult = ordenacao.ordem === 'asc' ? 1 : -1;
        if (ordenacao.campo === 'nome')
          return mult * (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
        if (ordenacao.campo === 'percentual')
          return mult * ((a.percentual || 0) - (b.percentual || 0));
        if (ordenacao.campo === 'data')
          return mult * (parseDateBR(a.dataPrevisao) - parseDateBR(b.dataPrevisao));
        return 0;
      })
    : projetosFiltrados;

  const abrirNovo = () => {
    setProjetoEditando(null);
    setForm(FORM_VAZIO);
    setModalAberto(true);
  };

  const abrirEdicao = (projeto) => {
    setProjetoEditando(projeto);
    setForm({
      nome: projeto.nome || '',
      descricao: projeto.descricao || '',
      categoria: projeto.categoria || 'Desenvolvimento',
      status: projeto.status || 'aguardando',
      responsavel: projeto.responsavel || '',
      membros: (projeto.membros || []).join(', '),
      dataInicio: paraDataInput(projeto.dataInicio),
      dataPrevisao: paraDataInput(projeto.dataPrevisao),
    });
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setProjetoEditando(null);
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);

    const dados = {
      nome: form.nome,
      descricao: form.descricao,
      categoria: form.categoria,
      status: form.status,
      responsavel: form.responsavel,
      membros: form.membros
        .split(',')
        .map((m) => m.trim())
        .filter(Boolean),
      dataInicio: paraDataBR(form.dataInicio),
      dataPrevisao: paraDataBR(form.dataPrevisao),
    };

    if (projetoEditando) {
      await atualizarProjeto(projetoEditando.id, dados);
    } else {
      await criarProjeto(dados);
    }

    setSalvando(false);
    fecharModal();
  };

  const excluir = async (e, projeto) => {
    e.stopPropagation();
    if (window.confirm(`Tem certeza que deseja excluir o projeto "${projeto.nome}"?`)) {
      await excluirProjeto(projeto.id);
    }
  };

  if (carregando) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: cores.texto }}>Projetos</h1>
        <Btn onClick={abrirNovo}>+ Novo projeto</Btn>
      </div>

      {/* Filtros de status */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {FILTROS_STATUS.map((item) => (
          <button
            key={item.valor}
            onClick={() => setFiltro(item.valor)}
            style={{
              padding: '6px 14px',
              borderRadius: 999,
              border: `1px solid ${filtro === item.valor ? cores.primario : cores.card}`,
              backgroundColor: filtro === item.valor ? cores.primario : '#fff',
              color: filtro === item.valor ? '#fff' : cores.texto,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {item.rotulo}
          </button>
        ))}
      </div>

      {/* Ordenação */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: cores.textoSecundario, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Ordenar:
        </span>
        {ORDENACOES.map((op) => {
          const ativo = ordenacao.campo === op.campo;
          const direcaoRotulo = ativo
            ? (ordenacao.ordem === 'asc' ? op.asc : op.desc)
            : op.asc;
          return (
            <button
              key={op.campo}
              onClick={() => alternarOrdenacao(op.campo)}
              style={{
                padding: '5px 14px',
                borderRadius: 999,
                border: `1px solid ${ativo ? cores.primario : cores.card}`,
                backgroundColor: ativo ? cores.card : '#fff',
                color: ativo ? cores.primario : cores.textoSecundario,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <span>{op.rotulo}</span>
              <span style={{ opacity: ativo ? 1 : 0.4 }}>{direcaoRotulo}</span>
            </button>
          );
        })}
        {ordenacao.campo && (
          <button
            onClick={() => setOrdenacao({ campo: '', ordem: 'asc' })}
            style={{
              padding: '5px 10px',
              borderRadius: 999,
              border: `1px solid ${cores.card}`,
              backgroundColor: 'transparent',
              color: cores.textoTerciario,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            ✕ Limpar
          </button>
        )}
      </div>

      {projetosOrdenados.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum projeto encontrado"
          descricao="Crie um novo projeto ou ajuste o filtro selecionado."
          acao={<Btn onClick={abrirNovo}>+ Novo projeto</Btn>}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {projetosOrdenados.map((projeto) => {
            const atrasado = projeto.status === 'atrasado';
            const corBarra = projeto.status === 'concluida' ? cores.sucesso : atrasado ? cores.erro : cores.primario;
            return (
              <div
                key={projeto.id}
                onClick={() => navegar(`/projetos/${projeto.id}`)}
                style={{
                  backgroundColor: '#fff',
                  border: `1px solid ${atrasado ? cores.erroFundo : cores.card}`,
                  borderRadius: 12,
                  padding: 18,
                  cursor: 'pointer',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                <div style={{ flex: '2 1 220px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, fontSize: 15, color: cores.texto }}>{projeto.nome}</h3>
                    <BadgeCategoria categoria={projeto.categoria} />
                    <BadgeStatus status={projeto.status} />
                  </div>
                  {projeto.descricao && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        color: cores.textoSecundario,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {projeto.descricao}
                    </p>
                  )}
                </div>

                <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <strong style={{ color: cores.texto }}>{projeto.percentual || 0}%</strong>
                    <span style={{ color: cores.textoSecundario }}>
                      {projeto.tarefasConcluidas || 0}/{projeto.totalTarefas || 0}
                    </span>
                  </div>
                  <BarraProgresso percentual={projeto.percentual || 0} cor={corBarra} />
                </div>

                <div style={{ display: 'flex', flexShrink: 0 }}>
                  {(projeto.membros || []).slice(0, 4).map((membro, indice) => (
                    <div key={membro} style={{ marginLeft: indice === 0 ? 0 : -8 }}>
                      <Avatar nome={membro} size={28} />
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: 12, color: cores.textoSecundario, flexShrink: 0, minWidth: 110 }}>
                  📅 {projeto.dataPrevisao || '—'}
                </div>

                {(projeto.ownerUid === usuario?.uid || !projeto.ownerUid) && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <Btn variante="secundario" onClick={(e) => { e.stopPropagation(); abrirEdicao(projeto); }}>
                      Editar
                    </Btn>
                    <Btn variante="perigo" onClick={(e) => excluir(e, projeto)}>
                      Excluir
                    </Btn>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        titulo={projetoEditando ? 'Editar projeto' : 'Novo projeto'}
        aberto={modalAberto}
        aoFechar={fecharModal}
        largura={520}
      >
        <form onSubmit={salvar}>
          <Campo label="Nome*">
            <Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          </Campo>
          <Campo label="Descrição">
            <Input value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
          </Campo>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Campo label="Categoria">
              <Select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                {CATEGORIAS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Select>
            </Campo>
            <Campo label="Status">
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUS_OPCOES.map((s) => (
                  <option key={s.valor} value={s.valor}>{s.rotulo}</option>
                ))}
              </Select>
            </Campo>
          </div>
          <Campo label="Responsável">
            <Input value={form.responsavel} onChange={(e) => setForm({ ...form, responsavel: e.target.value })} />
          </Campo>
          <Campo label="Membros (separados por vírgula)">
            <Input
              value={form.membros}
              onChange={(e) => setForm({ ...form, membros: e.target.value })}
              placeholder="Ex: Eldo, Marco, Rodrigo"
            />
          </Campo>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Campo label="Data de início">
              <Input type="date" value={form.dataInicio} onChange={(e) => setForm({ ...form, dataInicio: e.target.value })} />
            </Campo>
            <Campo label="Previsão de entrega">
              <Input type="date" value={form.dataPrevisao} onChange={(e) => setForm({ ...form, dataPrevisao: e.target.value })} />
            </Campo>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Btn type="button" variante="secundario" onClick={fecharModal}>Cancelar</Btn>
            <Btn type="submit" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
